'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft,
  Send,
  Loader2,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ExternalLink,
} from 'lucide-react';
import {
  useAdminTicketDetail,
  useAdminReply,
  useAdminUpdateStatus,
  useMarkTicketSeen,
} from '../hooks/use-admin-support-inbox';
import {
  formatRelativeTime,
  statusLabel,
  statusToneClasses,
} from '../lib/format';

interface AdminThreadViewProps {
  ticketId: string;
  onBack: () => void;
  onResolved: () => void;
}

export function AdminThreadView({
  ticketId,
  onBack,
  onResolved,
}: AdminThreadViewProps) {
  const { data: ticket, isLoading } = useAdminTicketDetail(ticketId);
  const reply = useAdminReply(ticketId);
  const updateStatus = useAdminUpdateStatus(ticketId);
  const markSeen = useMarkTicketSeen(ticketId);
  const [body, setBody] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const messageCount = ticket?.messages?.length ?? 0;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messageCount]);

  // Marcheaza notificarile pentru acest tichet ca citite la mount + cand
  // numarul de mesaje creste (un mesaj nou a sosit prin polling). Backend
  // marcheaza atomic toate notif-urile cu data.ticketId = ticketId pentru
  // adminul curent.
  useEffect(() => {
    if (!ticket) return;
    markSeen.mutate();
    // markSeen e stabil (returnat din useMutation), nu il punem in deps ca sa
    // evitam re-run-uri redundante. ticketId schimbat → component remount →
    // useEffect ruleaza din nou.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id, messageCount]);

  if (isLoading || !ticket) {
    return (
      <div className="flex flex-1 items-center justify-center text-neutral-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  async function handleSend() {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await reply.mutateAsync(trimmed);
      setBody('');
    } catch {
      /* error reflectat în reply.isError */
    }
  }

  async function handleResolve() {
    try {
      await updateStatus.mutateAsync('resolved');
      onResolved();
    } catch {
      /* idem */
    }
  }

  async function handleReopen() {
    try {
      await updateStatus.mutateAsync('open');
    } catch {
      /* idem */
    }
  }

  const isOpen =
    ticket.status === 'open' ||
    ticket.status === 'pending_user' ||
    ticket.status === 'pending_agent';

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-start gap-2 border-b border-neutral-200 px-3 py-2.5 dark:border-neutral-800">
        <button
          type="button"
          onClick={onBack}
          aria-label="Înapoi la inbox"
          className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[0.75rem] font-semibold text-neutral-900 dark:text-neutral-100">
              {ticket.user?.name ?? 'Client'}
            </span>
            <span className="truncate text-[0.625rem] text-neutral-400 dark:text-neutral-600">
              · {ticket.tenant?.name ?? '—'}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[0.75rem] font-medium text-neutral-700 dark:text-neutral-300">
            {ticket.subject}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[0.625rem] text-neutral-500 dark:text-neutral-500">
            <span
              className={`rounded-full px-1.5 py-0.5 text-[0.5625rem] font-medium ${statusToneClasses(ticket.status)}`}
            >
              {statusLabel(ticket.status)}
            </span>
            <span>· {formatRelativeTime(ticket.updatedAt)}</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          {isOpen && (
            <button
              type="button"
              onClick={handleResolve}
              disabled={updateStatus.isPending}
              title="Marchează rezolvat"
              className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[0.625rem] font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-300"
            >
              <CheckCircle2 className="h-2.5 w-2.5" strokeWidth={2} />
              Rezolvat
            </button>
          )}
          {ticket.status === 'resolved' && (
            <button
              type="button"
              onClick={handleReopen}
              disabled={updateStatus.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-[0.625rem] font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300"
            >
              <RotateCcw className="h-2.5 w-2.5" strokeWidth={2} />
              Redeschide
            </button>
          )}
          {ticket.status !== 'closed' && (
            <button
              type="button"
              onClick={() => updateStatus.mutate('closed')}
              disabled={updateStatus.isPending}
              title="Închide tichet"
              className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-[0.625rem] font-medium text-neutral-500 hover:bg-neutral-100 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-400"
            >
              <XCircle className="h-2.5 w-2.5" strokeWidth={2} />
              Închide
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {ticket.messages?.map((msg) => {
          const isAgent = msg.authorType === 'agent';
          const isSystem = msg.authorType === 'system' || msg.authorType === 'ai';
          return (
            <div
              key={msg.id}
              className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-[0.75rem] leading-relaxed ${
                  isAgent
                    ? 'bg-amber-500 text-white'
                    : isSystem
                      ? 'bg-neutral-100 italic text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400'
                      : 'border border-neutral-200 bg-white text-neutral-900 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100'
                }`}
              >
                {!isAgent && !isSystem && (
                  <div className="mb-0.5 text-[0.5625rem] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">
                    {ticket.user?.name ?? 'Client'}
                  </div>
                )}
                {isAgent && (
                  <div className="mb-0.5 text-[0.5625rem] font-medium uppercase tracking-wider text-amber-100">
                    Tu (agent)
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words">{msg.body}</div>
                <div
                  className={`mt-0.5 text-[0.5625rem] ${
                    isAgent
                      ? 'text-amber-100/80'
                      : 'text-neutral-400 dark:text-neutral-600'
                  }`}
                >
                  {formatRelativeTime(msg.createdAt)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reply box */}
      {ticket.status !== 'closed' && (
        <div className="border-t border-neutral-200 p-2.5 dark:border-neutral-800">
          <div className="flex items-end gap-2 rounded-xl border border-neutral-200 bg-white px-2.5 py-1.5 focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-100 dark:border-neutral-700 dark:bg-neutral-800 dark:focus-within:border-amber-500 dark:focus-within:ring-amber-950">
            <textarea
              rows={2}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={`Scrie ca agent către ${ticket.user?.name ?? 'client'}…`}
              className="flex-1 resize-none bg-transparent text-[0.75rem] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-none dark:text-neutral-100 dark:placeholder:text-neutral-500"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!body.trim() || reply.isPending}
              aria-label="Trimite (Cmd/Ctrl+Enter)"
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-amber-500 text-white transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400 dark:disabled:bg-neutral-700 dark:disabled:text-neutral-500"
            >
              {reply.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5" strokeWidth={1.75} />
              )}
            </button>
          </div>
          <div className="mt-1 flex items-center justify-between px-1 text-[0.5625rem] text-neutral-400 dark:text-neutral-600">
            <span>⌘/Ctrl + Enter</span>
            {ticket.user?.email && (
              <span>Trimitem și pe email: {ticket.user.email}</span>
            )}
          </div>
        </div>
      )}

      {/* Footer link to full admin page */}
      <div className="border-t border-neutral-200/70 bg-white/40 px-3 py-1.5 backdrop-blur-sm dark:border-neutral-800/70 dark:bg-neutral-900/40">
        <a
          href={`/admin/support/${ticketId}`}
          className="inline-flex items-center gap-1 text-[0.625rem] font-medium text-neutral-500 hover:text-amber-700 dark:text-neutral-500 dark:hover:text-amber-400"
        >
          Vezi tot tichetul cu context (firmă, browser, etc.)
          <ExternalLink className="h-2.5 w-2.5" strokeWidth={2} />
        </a>
      </div>
    </div>
  );
}
